// Giving money back, wholly or by the line: ten books were paid for, five
// were in the warehouse, so five go back.
//
// It sits with the store's other admin screens — products, purchases,
// transactions — because refunding is something a shop does to an order, and
// it is answered by the store module on the other side
// (aldoo-na/store/refunds).
import React, { useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Checkbox from "aldoo-ra/Checkbox"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { ExclamationTriangleIcon, TruckIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { describeLine } from "aldoo-ra/Store/lines"

// labels in English: each is said through the dialog's t()
const MODES = [
  { value: "lines", label: "Selected items" },
  { value: "amount", label: "Amount" },
]
// how an amount is said: all that is left, a share of it, or a sum
const PRESETS = [
  { value: "full", label: "Full amount" },
  { value: "percent", label: "Percent" },
  { value: "fixed", label: "Fixed" },
]

/** A number as typed: digits, and at most two decimals after a point or a comma. */
const DECIMAL = /^\d+([.,]\d{1,2})?$/
const parseDecimal = (text) => (DECIMAL.test(String(text).trim()) ? parseFloat(String(text).trim().replace(",", ".")) : NaN)

/**
 * What a money or percent field keeps of what is typed: digits and one decimal
 * point (a comma is taken as one), at most 2 decimals — "12,5x" is "12.5".
 */
const decimalTyped = (text) => {
  const clean = String(text ?? "").replace(/,/g, ".").replace(/[^0-9.]/g, "")
  const [whole, ...rest] = clean.split(".")
  if (!rest.length) return whole
  return `${whole || "0"}.${rest.join("").slice(0, 2)}`
}

/** The field as it reads once left: money in the 0.00 form, a percent without a trailing point. */
const decimalShown = (text, places) => {
  // "12." is 12: the point typed on the way to the decimals is let go
  const value = parseDecimal(String(text ?? "").replace(/\.$/, ""))
  if (!Number.isFinite(value)) return String(text ?? "")
  return places ? value.toFixed(places) : String(Math.round(value * 100) / 100)
}

/** A segmented control: one of a few forms of the same choice, its parts equal. */
const Segmented = ({ options, value, onChange, t, admin, dim, small = false }) => (
  <div className={twMerge("grid p-1 rounded-lg gap-1", admin ? "bg-admin_neutral_1" : "bg-neutral_1")} style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }} role="tablist">
    {options.map((option) => {
      const active = value === option.value
      return (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(option.value)}
          className={twMerge(
            "px-3 rounded-md text-center transition-colors",
            small ? "h-8 text-xs" : "h-9 text-sm",
            active ? twMerge("font-semibold shadow-sm", admin ? "bg-admin_paper text-admin_text" : "bg-paper text-text") : twMerge(dim, admin ? "hover:text-admin_text" : "hover:text-text")
          )}
          data-testid={`refund-option-${option.value}`}
        >
          {t(option.label)}
        </button>
      )
    })}
  </div>
)

/** English with its %s filled in: the words when no `t` is given. */
const english = (text, ...args) => {
  if (Array.isArray(text)) text = text[0]
  let i = 0
  return String(text ?? "").replace(/%s/g, () => (i < args.length ? String(args[i++]) : "%s"))
}

/** Minor units as money, in the order's currency. */
export const money = (amount, currency) => `${((amount ?? 0) / 100).toFixed(2)}${currency ? ` ${String(currency).toUpperCase()}` : ""}`

/** A line's key: its own (two sizes of one product are two lines), else its product's. */
const keyOf = (line) => line?.lineKey || line?.pid

/**
 * How many of each cart line are left, by its place in the cart: the server's
 * reading of the order after its refunds (`order.final`, aldoo-na/store/refunds
 * netOf) when the order carries it, else the refund lines counted here by key.
 */
const leftOf = (order) => {
  const cart = order?.cart || []
  if (order?.final?.lines) {
    const left = new Map(order.final.lines.map((line) => [line.cartIndex, line.quantity]))
    return cart.map((_, index) => left.get(index) ?? 0)
  }
  const given = new Map()
  for (const line of (order?.refunds || []).flatMap((refund) => refund.lines || [])) given.set(keyOf(line), (given.get(keyOf(line)) || 0) + (line.quantity ?? 0))
  return cart.map((item) => {
    const back = given.get(keyOf(item)) || 0
    const left = Math.max(0, (item.quantity ?? 1) - back)
    given.set(keyOf(item), Math.max(0, back - (item.quantity ?? 1)))
    return left
  })
}

/**
 * What a line was really paid: a discount taken off the whole order (a promo
 * code) belongs to no line, so it is shared out across them in proportion —
 * the same arithmetic the store does when the refund is made
 * (aldoo-na/store/refunds `lineShare`).
 */
const lineShare = (order) => {
  const lines = (order?.cart || []).reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0)
  const discount = order?.discountAmount ?? 0
  if (!lines || discount <= 0) return 1
  return Math.max(0, (lines - discount) / lines)
}

/**
 * `t`: the words, as the order screens say them (StoreOrders `useTexts`):
 * t`English` or t("English %s", value); English when not given.
 * `onRefund({ lines, amount, shipping, reason })` issues it, once confirmed (the
 * note to the buyer, asked for then, is its reason) — no `lines` and no
 * `amount` means the whole remaining order (the Amount tab's full amount, or a
 * percent or sum that comes to all of it), `amount` a sum of money rather than
 * goods — and answers `{ error, message }` or the saved order.
 * `initial`: units picked when it opens, by cart index (`{ 0: 2 }`) — the
 * goods that just came back, when it opens after a return.
 */
export default function RefundDialog({ order, onClose, onRefund, variant = "admin", t = english, height = null, initial = null }) {
  const admin = variant.includes("admin")
  // a text by its key, for a word the dialog says twice with two meanings
  // ("Refund" the action, and "Refund [2] units"): English when the words have no such key
  const say = (key, fallback) => {
    const said = t(key)
    return said && said !== key ? said : fallback
  }
  const currency = order?.currency
  const refunded = order?.refundedAmount ?? 0
  const refundable = Math.max(0, (order?.amount ?? 0) - refunded)

  // what is left of each line, and how many of it to give back
  const lines = useMemo(() => {
    const share = lineShare(order)
    const left = leftOf(order)
    return (order?.cart || []).map((item, index) => {
      const price = item.price ?? 0
      return {
        // a line of the cart, by its place: two sizes of one product are two lines
        id: String(index),
        pid: item.pid,
        lineKey: item.lineKey || null,
        // with what was chosen on it: "Jacket (Size: M)"
        name: describeLine(item, item.name || item.pid),
        thumbnail: item.thumbnail || null,
        price,
        // what one of it gives back: its price, less its share of an
        // order-wide discount
        refundPrice: Math.round(price * share),
        originalPrice: item.originalPrice ?? null,
        discountPercent: item.discountPercent ?? null,
        bought: item.quantity ?? 1,
        left: left[index],
        // whether it travels: a line that does not say is a parcel
        ships: item.isPhysical !== false,
      }
    })
  }, [order])

  // "lines" · "amount"; an amount is all that is left, a percent of it, or a fixed sum
  const [mode, setMode] = useState("lines")
  const [preset, setPreset] = useState("full")
  // a Checkbox keeps its own state: a tick re-mounts the group, so a choice clicked again stays chosen
  const [presetTick, setPresetTick] = useState(0)
  const [percent, setPercent] = useState("")
  const [custom, setCustom] = useState("")
  // a field says what is wrong with it once it was typed in or left
  const [touched, setTouched] = useState({})
  const touch = (field) => setTouched((current) => ({ ...current, [field]: true }))

  // nothing is picked until the operator says how many of a line go back — or
  // what just came back, when it opens after a return
  const [quantities, setQuantities] = useState(() => (initial ? Object.fromEntries(Object.entries(initial).map(([index, quantity]) => [String(index), String(quantity)])) : {}))
  // a whole number of units, none to all that is left
  const clampQuantity = (value, left) => Math.max(0, Math.min(Math.floor(Number(value) || 0), left))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const picked = lines
    .map((line) => ({ ...line, quantity: clampQuantity(quantities[line.id], line.left) }))
    .filter((line) => line.quantity > 0)
  const pickedAmount = picked.reduce((sum, line) => sum + line.refundPrice * line.quantity, 0)

  // the delivery fee, an item of its own: what of it is not given back yet
  // (the server's reading, aldoo-na/store/refunds netOf)
  const shippingLeft = Math.min(Math.max(0, (order?.shippingCost ?? 0) - (order?.final?.totals?.refundedShipping ?? 0)), refundable)
  const pickedOf = (line) => picked.find((entry) => entry.id === line.id)?.quantity ?? 0
  // nothing left to ship once these go back: the delivery goes back with them,
  // as the store does it (it is set, and cannot be taken off)
  const nothingShips = picked.length > 0 && lines.filter((line) => line.ships && line.left > 0).every((line) => pickedOf(line) >= line.left)
  const deliveryLocked = shippingLeft > 0 && nothingShips
  // the delivery is a flag, not a count: on gives it back
  const [deliveryOn, setDeliveryOn] = useState(false)
  const deliveryPicked = shippingLeft > 0 && (deliveryLocked || deliveryOn)
  // the last of every line: all that is left of the payment goes back
  const everythingPicked = picked.length > 0 && lines.every((line) => pickedOf(line) >= line.left)
  const linesAmount = everythingPicked ? refundable : Math.min(pickedAmount + (deliveryPicked ? shippingLeft : 0), refundable)

  // the amount tab: all that is left, a percent of it (1–100), or a sum (more than 0, no more than is left)
  const percentValue = parseDecimal(percent)
  const percentError = preset === "percent" && !(percentValue >= 1 && percentValue <= 100) ? t`Enter a percent from 1 to 100, with at most 2 decimals` : null
  const fixedValue = parseDecimal(custom)
  const fixedAmount = Number.isFinite(fixedValue) ? Math.round(fixedValue * 100) : 0
  const fixedError =
    preset !== "fixed" ? null : !(fixedAmount > 0) ? t`Enter an amount above 0, with at most 2 decimals` : fixedAmount > refundable ? t("That is %s — more than is left to refund.", money(fixedAmount, currency)) : null
  const presetAmount = preset === "full" ? refundable : preset === "percent" ? (percentError ? 0 : Math.round((refundable * percentValue) / 100)) : fixedError ? 0 : fixedAmount
  const invalid = mode === "amount" && !!(percentError || fixedError)
  // what each way of saying the amount means, beside it
  const descriptionOf = (value) => {
    if (value === "percent") return t`a share of what is left`
    if (value === "fixed") return t`an exact sum`
    return shippingLeft > 0 ? t("everything left (%s), delivery included", money(refundable, currency)) : t("everything left (%s)", money(refundable, currency))
  }

  const amount = mode === "amount" ? Math.min(presetAmount, refundable) : linesAmount
  // all that is left, however it was said, is a full refund (the order is then refunded)
  const whole = mode === "amount" && amount === refundable

  /** Make the refund, the note to the buyer its reason (kept with the order, and told to them). */
  const send = async (note) => {
    setBusy(true)
    setError(null)
    try {
      const res = await onRefund({
        lines: mode === "lines" ? picked.map(({ pid, lineKey, quantity }) => ({ pid, ...(lineKey ? { lineKey } : {}), quantity })) : undefined,
        // the delivery fee, on its own or with the goods
        shipping: mode === "lines" && deliveryPicked ? true : undefined,
        // a share or a sum of money; all of what is left is the whole order (no amount: lines, delivery and all)
        amount: mode === "amount" && !whole ? amount : undefined,
        reason: String(note || "").trim() || undefined,
      })
      if (res?.error) return setError(res.message || res.error)
      onClose?.(res)
    } catch (failure) {
      setError(failure?.message || t`The refund could not be made`)
    } finally {
      setBusy(false)
    }
  }

  // money goes back only once it is confirmed, with a note to the buyer if there is one
  const confirm = () => {
    let note = ""
    showPromptDialog({
      variant: admin ? "admin" : "default",
      title: t("Refund order %s", order?.orderId || ""),
      message: (
        <div className="flex flex-col gap-3">
          <span>{t("Refund %s to the customer?", money(amount, currency))}</span>
          <label className="flex flex-col gap-1">
            <span className={`text-xs ${dim}`}>{t`Note to the customer (optional)`}</span>
            <textarea
              rows={3}
              onChange={(e) => (note = e.target.value)}
              placeholder={t`Out of stock, damaged in transit…`}
              className={twMerge("px-3 py-2 rounded-md border text-sm outline-none resize-y", border, field)}
              data-testid="refund-note"
            />
          </label>
        </div>
      ),
      confirmText: t`Confirm`,
      cancelText: t`Cancel`,
      onConfirm: () => send(note),
    })
  }

  const label = admin ? "text-admin_text" : "text-text"
  const dim = admin ? "text-admin_text_secondary" : "text-text_secondary"
  const border = admin ? "border-admin_bordered" : "border-bordered"
  const field = admin ? "bg-admin_paper text-admin_text" : "bg-paper text-text"

  return (
    <Modal
      isOpen
      onClose={() => onClose?.()}
      variant={admin ? "admin_compact" : "compact"}
      title={t("Refund order %s", order?.orderId || "")}
      // the order dialog's height when it opened from it (its content scrolls inside)
      containerStyle={height ? { height: `${Math.round(height)}px` } : undefined}
      // the amount and the one action beside it stay in view; the rest scrolls above them
      // (the dialog closes with its X)
      footerContent={
        <div className="flex flex-wrap items-center gap-4">
          <Typography variant={admin ? "admin_body1" : "body"} className="my-0 ml-auto">
            <span className={dim}>{t`Refund amount:`} </span>
            <span className="font-semibold tabular-nums" data-testid="refund-amount">{money(amount, currency)}</span>
          </Typography>
          <Button variant={admin ? "admin_primary" : "primary"} onClick={confirm} disabled={busy || amount <= 0 || invalid} aria-label="refund-submit">
            {busy ? t`Refunding…` : t`Refund`}
          </Button>
        </div>
      }
      styleOverrides={{
        // the order's own size: it opens over the order dialog and takes its place
        container: `${admin ? "bg-admin_paper border-admin_bordered" : "bg-paper border-bordered"} w-full max-w-[900px] max-h-[94vh] overflow-hidden rounded-md border shadow-lg`,
        // the footer: the amount and the Refund button, under a line, always in view
        footer: `px-5 py-3 border-t ${admin ? "border-admin_bordered bg-admin_paper" : "border-bordered bg-paper"}`,
      }}
    >
      <div className="flex flex-col gap-4 p-5 pt-3">
        {/* paid on delivery: the courier paid the seller, so the seller pays the buyer back — this records it */}
        {order?.paymentMethod?.type === "cash_on_delivery" && (
          <div className={`rounded-lg px-4 py-3 text-sm ${admin ? "bg-admin_warning/15 text-admin_text" : "bg-neutral text-text"}`} data-testid="refund-outside-store">
            {t`Paid on delivery: pay the buyer back yourself. This records the refund, so your sales take it off.`}
          </div>
        )}
        {/* the order's money at a glance: what was paid, what went back, what is left to give */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="refund-summary">
          {[
            ["paid", t`Paid`, order?.amount ?? 0],
            ["returned", t`Refunded`, refunded],
            ["remaining", t`Remaining`, refundable],
          ].map(([key, title, value]) => (
            <div key={key} className={twMerge("flex flex-col rounded-xl border p-4 min-w-0", admin ? "border-admin_bordered bg-admin_paper" : "border-bordered bg-paper")} data-testid={`refund-summary-${key}`}>
              <div className={`text-xs font-semibold uppercase tracking-wide truncate ${dim}`}>{title}</div>
              <div className={`mt-2 text-[22px] leading-tight font-semibold tabular-nums ${label}`}>{money(value, currency)}</div>
            </div>
          ))}
        </div>

        {/* the two ways to refund, as one segmented control of equal parts:
            goods given back, or a sum of money */}
        <Segmented options={MODES} value={mode} onChange={setMode} t={t} admin={admin} dim={dim} />

        {mode === "lines" && (
          <div className={`border ${border} rounded-lg divide-y ${admin ? "divide-admin_bordered/60" : "divide-bordered/60"}`}>
            {/* only what can still go back: a line returned in full is not offered again */}
            {lines.filter((line) => line.left > 0).map((line) => (
              <div key={line.id} className="flex items-center gap-3 p-2.5 text-sm">
                {/* the line as the order shows it, picture and all */}
                <div className={twMerge("w-10 h-10 rounded overflow-hidden flex-shrink-0", admin ? "bg-admin_neutral_1" : "bg-neutral_1")}>
                  {line.thumbnail && <img src={line.thumbnail} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`truncate ${label}`}>{line.name}</div>
                  <div className={`text-xs ${dim} truncate`}>
                    {t("%s each", money(line.refundPrice, currency))}
                    {line.originalPrice > line.price && (
                      <>
                        {" · "}
                        <span className="line-through">{money(line.originalPrice, currency)}</span>
                        {line.discountPercent ? ` −${line.discountPercent}%` : ""}
                      </>
                    )}
                    {line.refundPrice !== line.price && <> · {t`share of the order discount taken off`}</>}
                    {" · "}
                    {t("%s of %s left", line.left, line.bought)}
                  </div>
                </div>
                {/* "Refund [ 0 ] units": each line starts at none */}
                <label className={twMerge("flex items-center gap-2 flex-shrink-0", !line.left && "opacity-50")}>
                  <span className={dim}>{say("refund_quantity_label", "Refund")}</span>
                  <input
                    type="number"
                    min={0}
                    max={line.left}
                    step={1}
                    disabled={!line.left}
                    value={quantities[line.id] ?? 0}
                    // typing may pass through "" or "1" on the way to "10"; more than is
                    // left is cut back at once, and on blur the field holds a whole number in range
                    onChange={(e) => {
                      const value = e.target.value
                      setQuantities((current) => ({ ...current, [line.id]: Number(value) > line.left ? String(line.left) : value }))
                    }}
                    onBlur={() => setQuantities((current) => ({ ...current, [line.id]: String(clampQuantity(current[line.id], line.left)) }))}
                    className={twMerge("w-[64px] h-9 px-2 rounded-md border text-right tabular-nums", border, field)}
                    data-testid="refund-line-quantity"
                  />
                  <span className={dim}>{say("refund_quantity_units", "units")}</span>
                </label>
              </div>
            ))}
            {/* the delivery fee, an item like the goods: given back on its own, or
                set (and kept) once nothing is left to ship */}
            {shippingLeft > 0 && (
              <div className="flex items-center gap-3 p-2.5 text-sm" data-testid="refund-delivery">
                <div className={twMerge("w-10 h-10 rounded flex items-center justify-center flex-shrink-0", admin ? "bg-admin_neutral_1" : "bg-neutral_1", dim)}>
                  <TruckIcon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`truncate ${label}`}>{t`Delivery`}</div>
                  <div className={`text-xs ${dim} truncate`}>
                    {money(shippingLeft, currency)}
                    {deliveryLocked && <> · {t`Nothing is left to ship, so the delivery goes back too`}</>}
                  </div>
                </div>
                {/* on: the delivery goes back; set and held once nothing is left to ship */}
                <div className={twMerge("flex-shrink-0", deliveryLocked && "opacity-60 pointer-events-none")} aria-disabled={deliveryLocked} data-testid="refund-delivery-toggle">
                  <ToggleSwitch variant={admin ? "admin" : "default"} isOn={deliveryPicked} onChange={(on) => setDeliveryOn(on)} />
                </div>
              </div>
            )}
          </div>
        )}

        {mode === "amount" && (
          <div className="flex flex-col gap-3" data-testid="refund-amount-tab">
            {/* one of three, each on its row with what it means; the chosen one's field under it */}
            <div className={`flex flex-col border ${border} rounded-lg divide-y ${admin ? "divide-admin_bordered/60" : "divide-bordered/60"}`} role="radiogroup">
              {PRESETS.map((option) => (
                <div key={option.value} className="flex flex-col gap-2 p-3" data-testid={`refund-option-${option.value}`}>
                  <Checkbox
                    // a round check used as a radio: picking one is the choice, picking it again keeps it
                    key={`${option.value}-${preset}-${presetTick}`}
                    variant={admin ? "admin_compact" : "compact"}
                    // the compact check's label is a caption (text-xs); these choices read as the rest of the dialog
                    labelClassName="text-sm ml-2"
                    isOn={preset === option.value}
                    onChange={() => {
                      setPreset(option.value)
                      setPresetTick((tick) => tick + 1)
                    }}
                    label={
                      <span>
                        <span className={`font-semibold ${label}`}>{t(option.label)}</span>
                        <span className={`font-normal ${dim}`}> — {descriptionOf(option.value)}</span>
                      </span>
                    }
                  />
                  {preset === option.value && option.value === "percent" && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={percent}
                    onChange={(e) => {
                      setPercent(decimalTyped(e.target.value))
                      touch("percent")
                    }}
                    onBlur={() => {
                      setPercent((current) => decimalShown(current, 0))
                      touch("percent")
                    }}
                    placeholder="0"
                    className={twMerge("w-[100px] h-9 px-3 rounded-md border text-sm text-right tabular-nums outline-none", border, field, touched.percent && percentError && (admin ? "border-admin_error" : "border-error"))}
                    aria-label="refund-percent"
                    data-testid="refund-percent"
                  />
                  <span className={`text-sm ${dim}`}>%</span>
                </div>
                {touched.percent && percentError && <span className={admin ? "text-xs text-admin_error" : "text-xs text-error"}>{percentError}</span>}
              </div>
                  )}
                  {preset === option.value && option.value === "fixed" && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={custom}
                    onChange={(e) => {
                      setCustom(decimalTyped(e.target.value))
                      touch("fixed")
                    }}
                    onBlur={() => {
                      setCustom((current) => decimalShown(current, 2))
                      touch("fixed")
                    }}
                    placeholder="0.00"
                    className={twMerge("w-[140px] h-9 px-3 rounded-md border text-sm text-right tabular-nums outline-none", border, field, touched.fixed && fixedError && (admin ? "border-admin_error" : "border-error"))}
                    aria-label="refund-fixed"
                    data-testid="refund-fixed"
                  />
                  <span className={`text-sm ${dim}`} data-testid="refund-currency">
                    {String(currency).toUpperCase()}
                  </span>
                </div>
                {touched.fixed && fixedError && <span className={admin ? "text-xs text-admin_error" : "text-xs text-error"}>{fixedError}</span>}
              </div>
                  )}
                </div>
              ))}
            </div>
            {/* what giving money back means, once, under the choices */}
            <span className={`text-xs ${dim}`}>{t`A refund is given back in the currency the order was charged in, and nothing goes back on the shelf.`}</span>
          </div>
        )}

        {/* what the card network said — a refused refund (no funds on the
            account, a closed charge) is the one thing the operator must not
            miss, so it is said as a notice, not as a line of small print */}
        {error && (
          <div
            role="alert"
            className={twMerge(
              "flex items-start gap-2 rounded-md border p-3 text-sm",
              admin ? "border-admin_error/40 bg-admin_error/10 text-admin_error" : "border-error/40 bg-error/10 text-error"
            )}
            data-testid="refund-error"
          >
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
            <div>
              <div className="font-semibold">{t`The refund was not made`}</div>
              <div>{error}</div>
            </div>
          </div>
        )}

      </div>
    </Modal>
  )
}
